import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/Card';

const API_BASE = 'https://api.glowytics.com';

export default function MetaCatalogProducts() {
  const { selectedBrand } = useAuth();
  const DEMO_BRAND_IDS_META = new Set(['2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a', 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4']);
  const SPECIAL_GA_ECOM_BRAND_ID = '062be473-3b2b-4bad-a2e3-a31995e02d57';
  const [catalogId, setCatalogId] = useState('');
  const [catalogName, setCatalogName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [nextCursor, setNextCursor] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [activeDetails, setActiveDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsById, setDetailsById] = useState({});
  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [sets, setSets] = useState([]);
  const [loadingSets, setLoadingSets] = useState(false);
  const [selectedSetId, setSelectedSetId] = useState('');
  const listRef = useRef(null);
  const scrollStateRef = useRef({ nextCursor: '', loadingMore: false, loading: false });
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newSetName, setNewSetName] = useState('glowytics');
  const [newSetRetailerId, setNewSetRetailerId] = useState('108234');
  const [customRulesJson, setCustomRulesJson] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [errorCreate, setErrorCreate] = useState('');
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [starsCount, setStarsCount] = useState(null);
  const [qmCount, setQmCount] = useState(null);
  const [ccCount, setCcCount] = useState(null);
  const [suggestIds, setSuggestIds] = useState({
    Stars: [],
    'Question Marks': [],
    'Cash Cows': []
  });
  const [suggestNames, setSuggestNames] = useState({
    Stars: [],
    'Question Marks': [],
    'Cash Cows': []
  });
  const [resolvedContentIdsByTitle, setResolvedContentIdsByTitle] = useState({
    Stars: {},
    'Question Marks': {},
    'Cash Cows': {}
  });
  const [suggestCounts, setSuggestCounts] = useState({
    stars: null,
    qm: null,
    cc: null
  });
  const [suggestState, setSuggestState] = useState({
    Stars: { status: 'idle', message: '' },
    'Question Marks': { status: 'idle', message: '' },
    'Cash Cows': { status: 'idle', message: '' }
  });
  // Tracks which BCG sets already exist in DB (category → meta_product_set_id)
  const [savedBcgSets, setSavedBcgSets] = useState({});
  const canRun = !!selectedBrand?.id && !!catalogId;

  const resolveCatalogFromConnections = async () => {
    if (!selectedBrand?.id) return;
    setError('');
    setCatalogId('');
    setCatalogName('');
    try {
      // DEMO: short-circuit catalog resolution for demo brands
      if (DEMO_BRAND_IDS_META.has(selectedBrand.id)) {
        setCatalogId('DEMO-CATALOG-TR');
        setCatalogName('Glowytics Demo Catalog');
        return;
      }
      const resp = await fetch(`${API_BASE}/api/auth/connections`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const connections = Array.isArray(data) ? data : (Array.isArray(data?.connections) ? data.connections : []);
      const metaCatalogConn = connections.find((c) => c?.platform === 'META_CATALOG' && c?.status === 'ACTIVE');
      if (!metaCatalogConn) {
        throw new Error('Active META_CATALOG connection not found. Please connect a catalog first.');
      }
      const resolvedCatalogId = metaCatalogConn.customerId || metaCatalogConn.catalogId || '';
      const resolvedCatalogName = metaCatalogConn.accountName || metaCatalogConn.catalogName || '';
      if (!resolvedCatalogId) {
        throw new Error('Catalog ID not found in saved connection.');
      }
      setCatalogId(resolvedCatalogId);
      setCatalogName(resolvedCatalogName);
    } catch (e) {
      setError(e.message || 'Failed to resolve catalog from connections');
    }
  };

  const fetchProductSets = async () => {
    if (!selectedBrand?.id || !catalogId) return;
    try {
      setLoadingSets(true);
      // DEMO: provide synthetic product sets
      if (DEMO_BRAND_IDS_META.has(selectedBrand.id) && String(catalogId).startsWith('DEMO-')) {
        const demoSets = [
          { id: 'ps_demo_all_shoes', name: 'All Shoes', product_count: 128 },
          { id: 'ps_demo_run_walk', name: 'Run & Walk', product_count: 64 },
          { id: 'ps_demo_outlet', name: 'Outlet Ayakkabı', product_count: 36 },
          { id: 'ps_demo_new_arrivals', name: 'Yeni Gelenler', product_count: 22 },
        ];
        setSets(demoSets);
        return;
      }
      const resp = await fetch(`${API_BASE}/api/meta-ads/catalogs/${encodeURIComponent(catalogId.trim())}/product-sets`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${resp.status}`);
      }
      const data = await resp.json().catch(() => ({}));
      const list = Array.isArray(data?.data) ? data.data : [];
      setSets(list);
    } catch (e) {
      // Non-fatal
    } finally {
      setLoadingSets(false);
    }
  };

  const fetchPage = async (afterCursor = '') => {
    if (!canRun) return;
    const isFirst = !afterCursor && products.length === 0;
    try {
      if (isFirst) setLoading(true);
      else setLoadingMore(true);
      // DEMO: synthesize product list with pagination-like chunks
      if (DEMO_BRAND_IDS_META.has(selectedBrand.id) && String(catalogId).startsWith('DEMO-')) {
        const startIdx = afterCursor ? Number(afterCursor) : 0;
        const pageSize = 50;
        const total = 140;
        const endIdx = Math.min(total, startIdx + pageSize);
        const imgPool = [
          'https://skcfiles.mncdn.com/livephotos/8/149807-OFWT/03.jpg',
          'https://cdn2.a101.com.tr/dbmk89vnr/CALL/Image/getcl/livephotos/8/26032267MAVI/1.jpg',
          'https://static.ticimax.cloud/3402/uploads/urunresimleri/buyuk/go-run-consistent-2-f55b-e.jpg',
          'https://skcfiles.mncdn.com/livephotos/8/232700tk-wht/1.jpg',
          'https://images.skechers.com/image;width=400%2Cformat=auto/150510_GYPK_HERO_LG',
          'https://media.kohlsimg.com/is/image/kohls/2528936_Gray_White_Leather?wid=1000&hei=1000&op_sharpen=1',
        ];
        const brands = ['Skechers','Nike','Adidas','Puma','New Balance','Hummel','Kinetix','Reebok','Asics','Lumberjack'];
        const demoRows = [];
        for (let i = startIdx; i < endIdx; i++) {
          const brand = brands[i % brands.length];
          const name = `${brand} Spor Ayakkabı ${100 + (i % 900)}`;
          const price = 999 + (i % 20) * 75;
          const sale = i % 4 === 0 ? price - 120 : null;
          const inStock = i % 7 !== 6;
          demoRows.push({
            id: `demo_${i}`,
            name,
            retailer_id: `SKU-${20000 + i}`,
            brand,
            price,
            sale_price: sale,
            currency: 'TRY',
            image_url: imgPool[i % imgPool.length],
            url: 'https://example.com/demo-ayakkabi',
            availability: inStock ? 'in stock' : 'out of stock',
            description: 'Demo ürün açıklaması',
          });
        }
        setProducts((prev) => [...prev, ...demoRows]);
        const next = endIdx < total ? String(endIdx) : '';
        setNextCursor(next);
        // Skip details prefetch in demo
        return;
      }
      const url = new URL(`${API_BASE}/api/meta-ads/catalogs/${encodeURIComponent(catalogId.trim())}/products`);
      url.searchParams.set('limit', '50');
      if (activeQuery) url.searchParams.set('q', activeQuery);
        if (selectedSetId) url.searchParams.set('productSetId', selectedSetId);
      if (afterCursor) url.searchParams.set('after', afterCursor);
      const resp = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const rows = Array.isArray(data?.data) ? data.data : [];
      const after = data?.paging?.cursors?.after || '';
      setProducts((prev) => [...prev, ...rows]);
      setNextCursor(after || '');

      // Prefetch details for the fetched rows (limit concurrency to avoid overload)
      prefetchDetails(rows);
    } catch (e) {
      setError(e.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const prefetchDetails = async (rows) => {
    if (!selectedBrand?.id || !Array.isArray(rows) || rows.length === 0) return;
    // DEMO: do not fetch details for demo to avoid network calls
    if (DEMO_BRAND_IDS_META.has(selectedBrand.id) && String(catalogId).startsWith('DEMO-')) {
      return;
    }
    const ids = rows.map((r) => r.id).filter((id) => id && !detailsById[id]);
    if (ids.length === 0) return;

    const concurrency = 6;
    let idx = 0;

    const runWorker = async () => {
      while (idx < ids.length) {
        const current = ids[idx++];
        try {
          const resp = await fetch(`${API_BASE}/api/meta-ads/products/${encodeURIComponent(current)}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Brand-ID': selectedBrand.id
            }
          });
          if (!resp.ok) {
            // ignore errors silently for prefetch
            await resp.text().catch(() => {});
            continue;
          }
          const data = await resp.json().catch(() => ({}));
          const details = data?.data || null;
          if (!details) continue;

          // Cache details
          setDetailsById((prev) => ({ ...prev, [current]: details }));

          // Merge into product list
          setProducts((prev) =>
            prev.map((p) => {
              if (p.id !== current) return p;
              return {
                ...p,
                name: details.name || p.name,
                retailer_id: details.retailer_id || p.retailer_id,
                brand: details.brand || p.brand,
                price: details.price != null ? details.price : p.price,
                sale_price: details.sale_price != null ? details.sale_price : p.sale_price,
                currency: details.currency || p.currency,
                image_url: details.image_url || p.image_url,
                url: details.url || p.url,
                availability: details.availability || p.availability,
                description: details.description || p.description,
              };
            })
          );
        } catch {
          // ignore
        }
      }
    };

    const workers = [];
    for (let i = 0; i < concurrency; i++) {
      workers.push(runWorker());
    }
    await Promise.all(workers);
  };

  const fetchSavedBcgSets = async () => {
    if (!selectedBrand?.id || !catalogId) return;
    try {
      const resp = await fetch(
        `${API_BASE}/api/meta-ads/catalogs/${encodeURIComponent(catalogId)}/bcg-product-sets`,
        { credentials: 'include', headers: { 'X-Brand-ID': selectedBrand.id } }
      );
      const json = await resp.json().catch(() => ({}));
      const map = {};
      if (Array.isArray(json?.data)) {
        json.data.forEach(r => { map[r.category] = r.meta_product_set_id; });
      }
      setSavedBcgSets(map);
    } catch (_) {}
  };

  useEffect(() => {
    resolveCatalogFromConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  useEffect(() => {
    if (canRun) {
      // reset listing when catalog changes
      setProducts([]);
      setNextCursor('');
      fetchPage('');
      fetchProductSets();
      prefetchPortfolioSuggestionCounts();
      fetchSavedBcgSets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRun, catalogId, activeQuery, selectedSetId]);

  const openProductModal = async (product) => {
    // Do not refetch on click. Use prefetched details if available,
    // otherwise use the already listed fields.
    setActiveProduct(product);
    const cached = detailsById[product.id];
    const merged = cached || {
      id: product.id,
      name: product.name || null,
      retailer_id: product.retailer_id || null,
      brand: product.brand || null,
      price: product.price != null ? product.price : null,
      sale_price: product.sale_price != null ? product.sale_price : null,
      currency: product.currency || null,
      availability: product.availability || null,
      description: product.description || null,
      image_url: product.image_url || null,
      url: product.url || null
    };
    setActiveDetails(merged);
    setLoadingDetails(false);
    setIsModalOpen(true);
  };

  // Keep ref in sync with latest scroll-related state so the scroll handler
  // always reads current values without being added/removed from the DOM on
  // every state change (avoids stale closure bug).
  useEffect(() => {
    scrollStateRef.current = { nextCursor, loadingMore, loading };
  }, [nextCursor, loadingMore, loading]);

  const fetchPageRef = useRef(null);
  fetchPageRef.current = fetchPage;

  // Infinite scroll handler — mounted once, reads state through refs
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      const { nextCursor: cursor, loadingMore: lm, loading: l } = scrollStateRef.current;
      if (!cursor || lm || l) return;
      const threshold = 100;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        fetchPageRef.current(cursor);
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const createProductSet = async () => {
    if (!selectedBrand?.id || !catalogId) return;
    setCreateLoading(true);
    setErrorCreate('');
    try {
      let body = {
        name: newSetName || 'glowytics',
      };
      if (customRulesJson && customRulesJson.trim()) {
        try {
          const parsed = JSON.parse(customRulesJson);
          body.filter = parsed;
        } catch (e) {
          setErrorCreate('Invalid JSON. Please provide valid rules.');
          setCreateLoading(false);
          return;
        }
      } else {
        body.retailerId = newSetRetailerId?.toString().trim() || '108234';
      }

      const resp = await fetch(`${API_BASE}/api/meta-ads/catalogs/${encodeURIComponent(catalogId.trim())}/product-sets?renameIfExists=1`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify(body)
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setErrorCreate(data?.error || data?.details || `HTTP ${resp.status}`);
        return;
      }
      await resp.json().catch(() => ({}));
      // Refresh sets
      await fetchProductSets();
      setIsCreateOpen(false);
    } catch (e) {
      setErrorCreate(e.message || 'Failed to create product set');
    } finally {
      setCreateLoading(false);
    }
  };

  // Suggested items per BCG category.
  //
  // This used to re-implement the whole BCG algorithm in the browser off the
  // live /monthly-item-performance feed. That second implementation drifted
  // from the canonical one (measured: 199 stars here vs 159 in the nightly
  // snapshot for the same brand and month) and carried the same defects the
  // backend has since fixed. It now reads the single source of truth:
  // bcg_product_categories, via /bcg-analysis/category-products.
  const fetchPortfolioCategoryItems = async (category) => {
    const brandHeader = selectedBrand?.id;
    if (!brandHeader) throw new Error('No brand selected');

    const headers = { 'Content-Type': 'application/json', 'X-Brand-ID': brandHeader };

    // Which grouping does this brand have? The catalog panel matches on
    // retailer/group IDs, so prefer the variant-group rollup when the nightly
    // job produced one.
    let grouping = 'item';
    try {
      const metaResp = await fetch(`${API_BASE}/api/google-ads/bcg-analysis?months=1&productLimit=1`, {
        method: 'GET', credentials: 'include', headers
      });
      if (metaResp.ok) {
        const meta = await metaResp.json();
        if (Array.isArray(meta?.availableGroupings) && meta.availableGroupings.includes('group')) {
          grouping = 'group';
        }
      }
    } catch {
      // Fall back to per-SKU; not worth failing the whole panel over.
    }

    const url = `${API_BASE}/api/google-ads/bcg-analysis/category-products`
      + `?month=reference&category=${encodeURIComponent(category)}&grouping=${grouping}`;
    const resp = await fetch(url, { method: 'GET', credentials: 'include', headers });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data?.error || data?.details || `HTTP ${resp.status}`);
    }
    const payload = await resp.json();
    const products = Array.isArray(payload?.products) ? payload.products : [];

    return products
      .map(p => ({
        id: String(p.mcItemGroupId && grouping === 'group' ? p.mcItemGroupId : (p.itemId || p.productId || '')),
        name: String(p.productName || '').trim() || null
      }))
      .filter(x => x.id);
  };

  // (Removed older duplicate count prefetcher; using unified function below)

  const createSetFromSuggestion = async (setName, category) => {
    try {
      // Mark creating for this category
      setSuggestState(prev => ({ ...prev, [category]: { status: 'creating', message: '' } }));
      setCreateLoading(true);
      setErrorCreate('');
      // Use prefetched items if available; otherwise compute on the fly
      let ids = suggestIds[category];
      let names = suggestNames[category];
      if (!Array.isArray(ids) || ids.length === 0) {
        const items = await fetchPortfolioCategoryItems(category);
        ids = Array.isArray(items) ? items.map((x) => String(x.id)) : [];
        // Keep index alignment with ids for debugging (use empty string when name missing)
        names = Array.isArray(items) ? items.map((x) => (x.name ? String(x.name) : '')) : [];
      }
      // Cache the computed IDs/names for debug visibility and to avoid recompute
      if (Array.isArray(ids) && ids.length > 0) {
        setSuggestIds((prev) => ({ ...prev, [category]: ids }));
        setSuggestNames((prev) => ({ ...prev, [category]: Array.isArray(names) ? names : [] }));
        setSuggestCounts((prev) => ({
          ...prev,
          stars: category === 'Stars' ? ids.length : prev.stars,
          qm: category === 'Question Marks' ? ids.length : prev.qm,
          cc: category === 'Cash Cows' ? ids.length : prev.cc
        }));
      }
      if (!ids || ids.length === 0) {
        setErrorCreate(`No products found for ${category}`);
        setSuggestState(prev => ({ ...prev, [category]: { status: 'error', message: 'No products found' } }));
        setCreateLoading(false);
        return;
      }
      // Send product names AND the portfolio item_ids. The backend resolves names to
      // Meta catalog IDs by title first; when titles don't match (GA4 item_name differs
      // from the Meta catalog title), it falls back to these item_ids as retailer_ids.
      const uniqueNames = Array.isArray(names)
        ? Array.from(new Set(names.map((s) => String(s).trim()).filter(Boolean)))
        : [];
      const uniqueIds = Array.isArray(ids)
        ? Array.from(new Set(ids.map((s) => String(s).trim()).filter(Boolean)))
        : [];
      const body = { name: setName, productNames: uniqueNames, retailerIds: uniqueIds };
      const resp = await fetch(`${API_BASE}/api/meta-ads/catalogs/${encodeURIComponent(catalogId.trim())}/product-sets?renameIfExists=1&resolveByTitle=1`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify(body)
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const errStats = data?.resolveStats || null;
        const errMsg = data?.error || data?.details || `HTTP ${resp.status}`;
        setErrorCreate(errMsg);
        setSuggestState(prev => ({ ...prev, [category]: { status: 'error', message: errMsg, resolveStats: errStats } }));
        setCreateLoading(false);
        return;
      }
      const result = await resp.json().catch(() => ({}));
      const createdId = result?.data?.id ? String(result.data.id) : null;
      const isDuplicate = Boolean(result?.data?.duplicate);
      const stats = result?.resolveStats || null;
      // Prefer the set's real product count; fall back to id-resolution count, then
      // title matches. ("namesMatched" alone is misleading when ids resolved the set —
      // GA4 item_name rarely equals the Meta title, so title matches are usually 0.)
      const totalCount = (typeof result?.totalCount === 'number') ? result.totalCount
        : (typeof result?.data?.totalCount === 'number') ? result.data.totalCount : null;
      let matchInfo = '';
      if (totalCount != null) {
        matchInfo = ` (${totalCount} ürün)`;
      } else if (stats && (stats.idType === 'retailer_id_fallback' || (stats.namesMatched === 0 && stats.idsUsed > 0))) {
        matchInfo = ` (${stats.idsUsed} ürün ID ile eşleşti)`;
      } else if (stats) {
        matchInfo = ` (${stats.namesMatched}/${stats.namesSent} eşleşti)`;
      }
      const wasUpdate = savedBcgSets[category] || result?.data?.bcgShortcut || result?.data?.updated;
      if (isDuplicate) {
        setSuggestState(prev => ({ ...prev, [category]: { status: 'exists', message: (createdId ? `Updated (${createdId})` : 'Updated') + matchInfo, resolveStats: stats } }));
      } else {
        const label = wasUpdate ? 'Updated' : 'Created';
        setSuggestState(prev => ({ ...prev, [category]: { status: 'success', message: (createdId ? `${label} (${createdId})` : label) + matchInfo, resolveStats: stats } }));
      }
      if (createdId) setSavedBcgSets(prev => ({ ...prev, [category]: createdId }));
      await fetchProductSets();
    } catch (e) {
      setErrorCreate(e.message || 'Failed to create set');
      setSuggestState(prev => ({ ...prev, [category]: { status: 'error', message: e.message || 'Failed' } }));
    } finally {
      setCreateLoading(false);
    }
  };

  const prefetchPortfolioSuggestionCounts = async () => {
    try {
      setSuggestionsLoading(true);
      const [starsItems, qmItems, ccItems] = await Promise.all([
        fetchPortfolioCategoryItems('Stars'),
        fetchPortfolioCategoryItems('Question Marks'),
        fetchPortfolioCategoryItems('Cash Cows'),
      ]);
      const starsIds = Array.isArray(starsItems) ? starsItems.map((x) => String(x.id)) : [];
      const qmIds = Array.isArray(qmItems) ? qmItems.map((x) => String(x.id)) : [];
      const ccIds = Array.isArray(ccItems) ? ccItems.map((x) => String(x.id)) : [];
      setSuggestIds({ Stars: starsIds, 'Question Marks': qmIds, 'Cash Cows': ccIds });
      setSuggestNames({
        // Keep index alignment with ids for debug display
        Stars: Array.isArray(starsItems) ? starsItems.map((x) => (x.name ? String(x.name) : '')) : [],
        'Question Marks': Array.isArray(qmItems) ? qmItems.map((x) => (x.name ? String(x.name) : '')) : [],
        'Cash Cows': Array.isArray(ccItems) ? ccItems.map((x) => (x.name ? String(x.name) : '')) : []
      });
      setSuggestCounts({
        stars: starsIds.length,
        qm: qmIds.length,
        cc: ccIds.length
      });
    } catch (_) {
      // ignore
    } finally {
      setSuggestionsLoading(false);
    }
  };
  // (Removed older duplicate useEffect for counts)
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <Card className="p-5 rounded-2xl shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
              <img src="/6033716.png" alt="Meta" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Meta Catalog Products</h1>
              {catalogId && (
                <p className="text-xs text-gray-400 mt-1 break-all">
                  Catalog: {catalogName || '—'} • {catalogId}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-xl text-xs bg-gray-100 text-gray-700">
              Loaded: {products.length}
            </span>
          </div>
        </div>
        {!selectedBrand?.id && <div className="mt-3 text-sm text-red-500">Please select a brand.</div>}
        {error && <div className="mt-3 text-sm text-red-500">{error}</div>}
      </Card>

      {/* Two-column layout */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: Product sets */}
        <Card className="rounded-xl lg:col-span-4 overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-medium text-gray-800">Product Sets</h2>
            <span className="text-xs text-gray-500">{loadingSets ? 'Loading…' : `${sets.length} sets`}</span>
          </div>
          {/* GlowyAI Suggestions */}
          <div className="px-3 pt-3 pb-2 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-medium border border-purple-200 text-purple-700 bg-white/70">
                <img src="/chat-gpt.svg" alt="GlowyAI" className="w-3.5 h-3.5" />
                GlowyAI suggestions
              </div>
              <button
                onClick={() => setIsCreateOpen(true)}
                className="px-2 py-0.5 rounded-full text-[10px] border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
              >
                Create Set
              </button>
            </div>
            <ul className="mt-2 space-y-1.5">
              <li className="px-2 py-1.5 rounded-lg bg-white border border-purple-100 text-sm text-gray-800 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  Glowytics_Stars
                  <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-700">
                    {suggestCounts.stars != null ? suggestCounts.stars : '—'}
                  </span>
                </span>
                <button
                  onClick={() => { setNewSetName('Glowytics_Stars'); createSetFromSuggestion('Glowytics_Stars', 'Stars'); }}
                  disabled={suggestState['Stars'].status === 'creating'}
                  className={`px-2 py-0.5 rounded-full text-[10px] border ${
                    suggestState['Stars'].status === 'creating'
                      ? 'border-gray-200 text-gray-500 bg-gray-50 cursor-not-allowed'
                      : 'border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100'
                  }`}
                >
                  {suggestState['Stars'].status === 'creating' ? (savedBcgSets['Stars'] ? 'Updating…' : 'Creating…') : (savedBcgSets['Stars'] ? 'Update Set' : 'Create Set')}
                </button>
                {suggestState['Stars'].status === 'success' && (
                  <span className="ml-2 text-[10px] text-emerald-700">{suggestState['Stars'].message || (savedBcgSets['Stars'] ? 'Updated' : 'Created')}</span>
                )}
                {suggestState['Stars'].status === 'exists' && (
                  <span className="ml-2 text-[10px] text-indigo-700" title={suggestState['Stars'].message}>
                    Exists
                  </span>
                )}
                {suggestState['Stars'].status === 'error' && (
                  <span className="ml-2 text-[10px] text-red-600" title={suggestState['Stars'].message}>Failed</span>
                )}
              </li>
              <li className="px-2 py-1.5 rounded-lg bg-white border border-purple-100 text-sm text-gray-800 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  Glowytics_QuestionMarks
                  <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-700">
                    {suggestCounts.qm != null ? suggestCounts.qm : '—'}
                  </span>
                </span>
                <button
                  onClick={() => { setNewSetName('Glowytics_QuestionMarks'); createSetFromSuggestion('Glowytics_QuestionMarks', 'Question Marks'); }}
                  disabled={suggestState['Question Marks'].status === 'creating'}
                  className={`px-2 py-0.5 rounded-full text-[10px] border ${
                    suggestState['Question Marks'].status === 'creating'
                      ? 'border-gray-200 text-gray-500 bg-gray-50 cursor-not-allowed'
                      : 'border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100'
                  }`}
                >
                  {suggestState['Question Marks'].status === 'creating' ? (savedBcgSets['Question Marks'] ? 'Updating…' : 'Creating…') : (savedBcgSets['Question Marks'] ? 'Update Set' : 'Create Set')}
                </button>
                {suggestState['Question Marks'].status === 'success' && (
                  <span className="ml-2 text-[10px] text-emerald-700">{suggestState['Question Marks'].message || (savedBcgSets['Question Marks'] ? 'Updated' : 'Created')}</span>
                )}
                {suggestState['Question Marks'].status === 'exists' && (
                  <span className="ml-2 text-[10px] text-indigo-700" title={suggestState['Question Marks'].message}>
                    Exists
                  </span>
                )}
                {suggestState['Question Marks'].status === 'error' && (
                  <span className="ml-2 text-[10px] text-red-600" title={suggestState['Question Marks'].message}>Failed</span>
                )}
              </li>
              <li className="px-2 py-1.5 rounded-lg bg-white border border-purple-100 text-sm text-gray-800 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  Glowytics_CashCows
                  <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-700">
                    {suggestCounts.cc != null ? suggestCounts.cc : '—'}
                  </span>
                </span>
                <button
                  onClick={() => { setNewSetName('Glowytics_CashCows'); createSetFromSuggestion('Glowytics_CashCows', 'Cash Cows'); }}
                  disabled={suggestState['Cash Cows'].status === 'creating'}
                  className={`px-2 py-0.5 rounded-full text-[10px] border ${
                    suggestState['Cash Cows'].status === 'creating'
                      ? 'border-gray-200 text-gray-500 bg-gray-50 cursor-not-allowed'
                      : 'border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100'
                  }`}
                >
                  {suggestState['Cash Cows'].status === 'creating' ? (savedBcgSets['Cash Cows'] ? 'Updating…' : 'Creating…') : (savedBcgSets['Cash Cows'] ? 'Update Set' : 'Create Set')}
                </button>
                {suggestState['Cash Cows'].status === 'success' && (
                  <span className="ml-2 text-[10px] text-emerald-700">{suggestState['Cash Cows'].message || (savedBcgSets['Cash Cows'] ? 'Updated' : 'Created')}</span>
                )}
                {suggestState['Cash Cows'].status === 'exists' && (
                  <span className="ml-2 text-[10px] text-indigo-700" title={suggestState['Cash Cows'].message}>
                    Exists
                  </span>
                )}
                {suggestState['Cash Cows'].status === 'error' && (
                  <span className="ml-2 text-[10px] text-red-600" title={suggestState['Cash Cows'].message}>Failed</span>
                )}
              </li>
            </ul>
          </div>
          <div className="p-2 max-h-[420px] overflow-y-auto">
            {loadingSets ? (
              <div className="p-4 text-sm text-gray-500">Loading…</div>
            ) : sets.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">No product sets found.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                <li
                  key="__all__"
                  className={`px-2 py-1.5 cursor-pointer ${!selectedSetId ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                  onClick={() => {
                    setSelectedSetId('');
                    setProducts([]);
                    setNextCursor('');
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate text-sm">All products</div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-700">
                      {/* Unknown total, leave blank */}
                      —
                    </span>
                  </div>
                </li>
                {sets.map((s) => (
                  <li
                    key={s.id}
                    className={`px-2 py-1.5 cursor-pointer ${selectedSetId === s.id ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                    onClick={() => {
                      setSelectedSetId(s.id);
                      setProducts([]);
                      setNextCursor('');
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate text-sm">{s.name || '—'}</div>
                        <div className="text-[11px] text-gray-500 truncate">{s.id}</div>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-indigo-50 text-indigo-700">
                        {typeof s.product_count === 'number' ? s.product_count : '—'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {/* Right: Products list */}
        <Card className="rounded-xl lg:col-span-8">
          <div className="p-4 border-b flex items-center justify-between gap-3">
            <h2 className="font-medium text-gray-800">Products</h2>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setProducts([]);
                    setNextCursor('');
                    setActiveQuery(query.trim());
                  }
                }}
                placeholder="Search products..."
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={() => {
                  setProducts([]);
                  setNextCursor('');
                  setActiveQuery(query.trim());
                }}
                className="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700"
              >
                Search
              </button>
              {activeQuery && (
                <button
                  onClick={() => {
                    setQuery('');
                    setActiveQuery('');
                    setProducts([]);
                    setNextCursor('');
                    fetchPage('');
                  }}
                  className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="p-4">
          {loading && products.length === 0 ? (
            <div className="text-sm text-gray-500">Loading products…</div>
          ) : products.length === 0 ? (
            <div className="text-sm text-gray-500">No products found.</div>
          ) : (
            <div ref={listRef} className="max-h-[640px] overflow-y-auto pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {products.map((p) => (
                <div
                  key={p.id}
                  className="flex gap-2 p-2 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-50"
                  onClick={() => openProductModal(p)}
                >
                  <div className="w-16 h-16 bg-gray-50 border border-gray-200 rounded overflow-hidden flex-shrink-0">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name || p.id} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No Image</div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate text-sm">{p.name || '—'}</div>
                    <div className="text-xs text-gray-500 truncate">{p.retailer_id || '—'}</div>
                    {/* Price and sale price */}
                    <div className="mt-1 text-xs">
                      {p.sale_price ? (
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-700 font-semibold">
                            {p.sale_price} {p.currency || ''}
                          </span>
                          {p.price && (
                            <span className="text-gray-400 line-through">
                              {p.price} {p.currency || ''}
                            </span>
                          )}
                        </div>
                      ) : p.price ? (
                        <span className="text-gray-800 font-medium">
                          {p.price} {p.currency || ''}
                        </span>
                      ) : null}
                    </div>
                    {/* Availability */}
                    {p.availability && (
                      <div className="mt-1">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] ${
                            String(p.availability).toLowerCase().includes('in stock')
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-gray-50 text-gray-700 border border-gray-200'
                          }`}
                        >
                          {p.availability}
                        </span>
                      </div>
                    )}
                    {/* Removed inline "View product" link to reduce clutter */}
                  </div>
                </div>
              ))}
              </div>
              {loadingMore && (
                <div className="py-3 text-center text-xs text-gray-500">Loading…</div>
              )}
            </div>
          )}
          {/* Infinite scroll loads more automatically; no button */}
          </div>
        </Card>
      </div>

      {/* Product Details Modal */}
      {isModalOpen && activeProduct && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => !loadingDetails && setIsModalOpen(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl bg-white rounded-xl shadow-lg border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 truncate">
                    {activeProduct.name || activeProduct.id}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">{activeProduct.retailer_id || '—'}</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                  disabled={loadingDetails}
                >
                  ✕
                </button>
              </div>
              <div className="px-5 py-4">
                <div className="flex gap-4">
                  <div className="w-32 h-32 bg-gray-50 border border-gray-200 rounded overflow-hidden flex-shrink-0">
                    {activeProduct.image_url ? (
                      <img src={activeProduct.image_url} alt={activeProduct.name || activeProduct.id} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No Image</div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {loadingDetails ? (
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-100 rounded w-2/3 animate-pulse" />
                        <div className="h-3 bg-gray-100 rounded w-1/2 animate-pulse" />
                        <div className="h-3 bg-gray-100 rounded w-1/3 animate-pulse" />
                        <div className="h-3 bg-gray-100 rounded w-3/4 animate-pulse" />
                      </div>
                    ) : activeDetails?.__error ? (
                      <div className="text-sm text-red-600">{activeDetails.__error}</div>
                    ) : activeDetails ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-gray-500">Retailer ID</div>
                          <div className="font-medium text-gray-900 break-words">
                            {activeDetails.retailer_id || activeProduct.retailer_id || '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Brand</div>
                          <div className="font-medium text-gray-900 break-words">
                            {activeDetails.brand || activeProduct.brand || '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Price</div>
                          <div className="font-medium text-gray-900 break-words">
                            {(activeDetails.price != null || activeProduct.price != null)
                              ? `${(activeDetails.price != null ? activeDetails.price : activeProduct.price)} ${activeDetails.currency || activeProduct.currency || ''}`
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Sale Price</div>
                          <div className="font-medium text-emerald-700 break-words">
                            {(activeDetails.sale_price != null || activeProduct.sale_price != null)
                              ? `${(activeDetails.sale_price != null ? activeDetails.sale_price : activeProduct.sale_price)} ${activeDetails.currency || activeProduct.currency || ''}`
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Availability</div>
                          <div className="font-medium text-gray-900 break-words">
                            {activeDetails.availability || activeProduct.availability || '—'}
                          </div>
                        </div>
                        <div className="sm:col-span-2">
                          <div className="text-gray-500">Description</div>
                          <div className="font-medium text-gray-900 break-words">
                            {activeDetails.description || activeProduct.description || '—'}
                          </div>
                        </div>
                        {(activeDetails.url || activeProduct.url) && (
                          <div className="sm:col-span-2">
                            <a
                              href={activeDetails.url || activeProduct.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-indigo-600 hover:underline"
                            >
                              View on site
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No details.</div>
                    )}
                  </div>
                </div>
                {/* Additional images */}
                {!loadingDetails && Array.isArray(activeDetails?.additional_image_urls) && activeDetails.additional_image_urls.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs text-gray-500 mb-2">Additional Images</div>
                    <div className="flex flex-wrap gap-2">
                      {activeDetails.additional_image_urls.slice(0, 8).map((u, idx) => (
                        <img key={idx} src={u} alt={`alt-${idx}`} className="w-16 h-16 rounded border object-cover" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={loadingDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Product Set Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => !createLoading && setIsCreateOpen(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-white rounded-xl shadow-lg border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">New Product Set</h3>
                  <button
                    onClick={() => setIsCreateOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                    disabled={createLoading}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="px-5 py-4">
                {errorCreate && (
                  <div className="mb-3 text-sm text-red-600">{errorCreate}</div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Name</label>
                    <input
                      type="text"
                      value={newSetName}
                      onChange={(e) => setNewSetName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">retailer_id equals</label>
                    <input
                      type="text"
                      value={newSetRetailerId}
                      onChange={(e) => setNewSetRetailerId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="mb-2 text-xs text-gray-500">
                    Alternative: If you provide valid JSON rules below (e.g. a large OR structure), retailer_id will be ignored.
                  </div>
                  <textarea
                    rows={6}
                    value={customRulesJson}
                    onChange={(e) => setCustomRulesJson(e.target.value)}
                    placeholder='Example: {"or":[{"retailer_id":{"eq":"79398"}},{"retailer_id":{"eq":"69544"}}]}'
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-xs"
                  />
                </div>
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
                <button
                  onClick={() => setIsCreateOpen(false)}
                  className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={createLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={createProductSet}
                  disabled={createLoading}
                  className={`px-4 py-2 rounded-md text-white ${createLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                >
                  {createLoading ? 'Creating…' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


